class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}

/**
 * Swiper host for product recommendations carousels.
 * Must be a custom element so it inits after AJAX section HTML injection.
 * mode=mobile-only: Swiper only below 750px; desktop uses CSS grid on the same DOM.
 * mode=always: Swiper on all viewports.
 */
class ProductRecommendationsSwiper extends HTMLElement {
  /** @type {any} */
  #swiper = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {MediaQueryList | null} */
  #mq = null;

  /** @type {(() => void) | null} */
  #onMqChange = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const mode = this.dataset.mode || 'mobile-only';

    if (mode === 'always') {
      this.#initSwiper();
      return;
    }

    this.#mq = window.matchMedia('(max-width: 749px)');
    this.#onMqChange = () => this.#syncForViewport();
    this.#mq.addEventListener('change', this.#onMqChange);
    this.#syncForViewport();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    if (this.#mq && this.#onMqChange) {
      this.#mq.removeEventListener('change', this.#onMqChange);
    }
    this.#mq = null;
    this.#onMqChange = null;
    this.#destroySwiper();
  }

  #syncForViewport() {
    if (!this.isConnected || this.#abortController?.signal.aborted) return;
    if (this.#mq?.matches) {
      this.#initSwiper();
    } else {
      this.#destroySwiper();
    }
  }

  /**
   * @param {number} [attempt]
   */
  #initSwiper(attempt = 0) {
    if (!this.isConnected || this.#abortController?.signal.aborted) return;

    const el = this.querySelector('[data-ott-recommendations-swiper]');
    if (!el) return;

    const SwiperCtor = globalThis.Swiper;
    if (typeof SwiperCtor !== 'function') {
      if (attempt < 40) {
        setTimeout(() => {
          if (!this.isConnected || this.#abortController?.signal.aborted) return;
          this.#initSwiper(attempt + 1);
        }, 50);
      }
      return;
    }

    this.#destroySwiper();
    this.#stabilizeCardImages();

    const spaceBetween = Number(this.dataset.spaceBetween || 12);
    const showArrows = this.dataset.showArrows !== 'false';
    const mode = this.dataset.mode || 'mobile-only';
    const columns = Number(this.dataset.columns || 0);
    const root = this;

    /** @type {Record<string, unknown>} */
    const config = {
      slidesPerView: 1.5,
      spaceBetween,
      grabCursor: true,
      watchOverflow: true,
      updateOnWindowResize: true,
      observer: true,
      observeParents: true,
      speed: 450,
    };

    if (mode === 'always') {
      config.breakpoints = {
        750: {
          slidesPerView: columns > 0 ? columns : 3.2,
          spaceBetween,
        },
      };
    }

    if (showArrows) {
      config.navigation = {
        nextEl: root.querySelector('.swiper-button-next'),
        prevEl: root.querySelector('.swiper-button-prev'),
      };
    }

    this.#swiper = new SwiperCtor(el, config);
    this.#syncNavA11y();

    this.#swiper.on('slideChange', () => this.#syncNavA11y());
    this.#swiper.on('resize', () => this.#stabilizeCardImages());
    this.#swiper.on('breakpoint', () => {
      requestAnimationFrame(() => {
        this.#stabilizeCardImages();
        this.#swiper?.update();
      });
    });
  }

  #destroySwiper() {
    if (!this.#swiper) return;
    this.#swiper.destroy(true, true);
    this.#swiper = null;
  }

  #stabilizeCardImages() {
    this.querySelectorAll('img[sizes]').forEach((img) => {
      const sizes = img.getAttribute('sizes');
      if (!sizes || !/^auto\s*,/i.test(sizes)) return;
      img.setAttribute('sizes', sizes.replace(/^auto\s*,\s*/i, ''));
    });

    this.querySelectorAll('img').forEach((img) => {
      if (img.complete && img.naturalWidth === 0 && img.src) {
        const { src } = img;
        img.removeAttribute('src');
        img.src = src;
      }
    });
  }

  #syncNavA11y() {
    const signal = this.#abortController?.signal;
    this.querySelectorAll('.swiper-button-prev, .swiper-button-next').forEach((btn) => {
      if (!(btn instanceof HTMLElement)) return;
      const disabled = btn.classList.contains('swiper-button-disabled');
      // Prefer aria-disabled over the native disabled attribute: native disabled
      // buttons are skipped by hit-testing and let taps fall through to product cards.
      btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      btn.setAttribute('tabindex', disabled ? '-1' : '0');
      btn.removeAttribute('aria-hidden');
      if (btn instanceof HTMLButtonElement) {
        btn.disabled = false;
      }
      if (signal && !btn.dataset.a11yBound) {
        btn.dataset.a11yBound = 'true';
        btn.addEventListener(
          'click',
          (event) => {
            if (btn.classList.contains('swiper-button-disabled')) {
              event.preventDefault();
              event.stopPropagation();
            }
            this.#syncNavA11y();
          },
          { signal }
        );
      }
    });
  }
}

if (!customElements.get('product-recommendations-swiper')) {
  customElements.define('product-recommendations-swiper', ProductRecommendationsSwiper);
}
